import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import languages from 'constants/languages'
import {
  detectBrowserLanguage,
  getBrowserInfo,
  getBrowserLanguages,
  mapToSupportedLanguage,
  supportedLngs,
} from 'utils/detectBrowserLanguage'

const LanguageDropdown = ({ variant = 'inline', showDebug = false }) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showTip, setShowTip] = useState(false)
  const [showDebugPanel, setShowDebugPanel] = useState(false)
  const containerRef = useRef(null)
  const lang = i18n.language || 'zh-TW'

  /**
   * 初始化時偵測並設置語言（不使用 localStorage）
   */
  useEffect(() => {
    // 使用自動偵測瀏覽器/系統語言
    const detectedLng = detectBrowserLanguage()

    if (showDebug) {
      const browserInfo = getBrowserInfo()
      console.log('[LanguageDropdown] Browser Info:', browserInfo)
      console.log('[LanguageDropdown] Detected Language:', detectedLng)
      console.log('[LanguageDropdown] Current i18n Language:', i18n.language)
    }

    // 如果偵測到的語言與當前語言不同，則切換
    if (detectedLng !== i18n.language) {
      i18n.changeLanguage(detectedLng)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * 設置 document lang 屬性
   */
  useLayoutEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language)
  }, [i18n.language])

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
        setShowDebugPanel(false)
      }
    }
    if (open || showDebugPanel) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open, showDebugPanel])

  // 匹配語言代碼，處理各種格式
  const findMatchingLanguage = (code) => {
    if (!code) return languages[0]

    // 完全匹配
    const exactMatch = languages.find((l) => l.code === code)
    if (exactMatch) return exactMatch

    // 標準化後匹配
    const normalizedCode = code.toLowerCase().replace('_', '-')
    const normalizedMatch = languages.find((l) => l.code.toLowerCase() === normalizedCode)
    if (normalizedMatch) return normalizedMatch

    // 主語言匹配（例如 zh-CN -> zh）
    const mainLang = code.split(/[-_]/)[0].toLowerCase()
    const mainLangMatch = languages.find((l) => l.code.toLowerCase().startsWith(mainLang))
    if (mainLangMatch) return mainLangMatch

    return languages[0]
  }

  const currentLanguage = findMatchingLanguage(lang)
  const currentShortLabel = currentLanguage.shortLabel

  const baseButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #dcdfe6',
    background: '#fff',
    color: '#303133',
    borderRadius: 16,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
  }

  /**
   * 處理語言切換
   */
  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode)
    setOpen(false)
    // 更新 document lang 屬性
    document.documentElement.setAttribute('lang', langCode)

    if (showDebug) {
      console.log('[LanguageDropdown] Language changed to:', langCode)
    }
  }

  /**
   * 檢查語言是否被選中
   */
  const isLanguageSelected = (langCode) => {
    if (lang === langCode) return true

    // 處理變體匹配
    const normalizedLang = lang.toLowerCase().replace('_', '-')
    const normalizedCode = langCode.toLowerCase().replace('_', '-')

    return normalizedLang === normalizedCode
  }

  /**
   * 渲染調試面板
   */
  const renderDebugPanel = () => {
    const browserInfo = getBrowserInfo()
    const detectedLng = detectBrowserLanguage()
    const browserLanguages = getBrowserLanguages()

    const getDeviceType = () => {
      const ua = navigator.userAgent
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|rim)|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          ua,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|ow)|az(er|ap|te)|bl(ac|k|l)|br(e|v)w|bumb|bw-(n|u)|c55|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(is|jp|it)|dc-s|dica|dmob|do(c|p)o|ds(12|-d)|el(at|te)|er(ic|k)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris9|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|cd|un)|mddl|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|ad|gr|ha|mm|te)(ly|vm)|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(787|801|802|803|8045|8079|a86|b70|r16|r20|r30)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|pn)|sc(01|ar|et|te)|sdk-|se(ca|fl)|hg|si(em|th)|smb(12|75)|sgh-|sr(ee|cd)|ss-4|st(ap|re|sv)|swac|tcl-|tdg-|tel(i|m)|tim-|tm(ga|ki)|ttg1|u.s.o|up(.b|si)|utst|v400|v750|veri|vi(rg|ty)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c( |-)|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
          ua.substr(0, 4),
        )
      ) {
        return 'Mobile'
      }
      if (/(ipad|tablet|android(?!.*mobile))/i.test(ua)) {
        return 'Tablet'
      }
      return 'Desktop'
    }

    const getOS = () => {
      const ua = navigator.userAgent
      if (ua.includes('Windows')) return 'Windows'
      if (ua.includes('Macintosh') || ua.includes('Mac OS X')) return 'macOS'
      if (ua.includes('Android')) return 'Android'
      if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
      if (ua.includes('Linux')) return 'Linux'
      return 'Unknown OS'
    }

    const getStatusColor = (status) => {
      if (status === 'en') return '#606266'
      if (status.startsWith('zh')) return '#409EFF'
      if (status.startsWith('ja')) return '#E6A23C'
      if (status.startsWith('vi')) return '#67C23A'
      return '#909399'
    }

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 100000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={() => setShowDebugPanel(false)}
      >
        <div
          style={{
            background: '#2f3640',
            color: '#fff',
            padding: 20,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            maxWidth: '90%',
            maxHeight: '90%',
            overflowY: 'auto',
            fontSize: 14,
            lineHeight: 1.6,
            fontFamily: 'monospace',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ color: '#409EFF', marginTop: 0, marginBottom: 15 }}>🔧 Debug Panel</h3>
          <button
            onClick={() => setShowDebugPanel(false)}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 20,
              cursor: 'pointer',
            }}
          >
            &times;
          </button>

          <p>
            <strong>當前顯示語系:</strong> <span style={{ color: getStatusColor(i18n.language), fontWeight: 'bold' }}>{i18n.language}</span>
          </p>
          <p>
            <strong>偵測結果語系:</strong> <span style={{ color: getStatusColor(detectedLng), fontWeight: 'bold' }}>{detectedLng}</span>
          </p>
          <p>
            <strong>裝置 & 瀏覽器:</strong> <span style={{ color: '#67C23A' }}>{getDeviceType()}</span> /{' '}
            <span style={{ color: '#E6A23C' }}>{getOS()}</span> / <span style={{ color: '#F56C6C' }}>{browserInfo.name}</span>{' '}
            {browserInfo.isWebView && <span style={{ color: '#909399' }}>(WebView)</span>}
          </p>
          <p>
            <strong>navigator.language:</strong> <span style={{ color: '#909399' }}>{navigator.language}</span>{' '}
            {mapToSupportedLanguage(navigator.language) && (
              <span style={{ color: '#409EFF' }}>(映射: {mapToSupportedLanguage(navigator.language)})</span>
            )}
          </p>
          <p>
            <strong>navigator.languages:</strong> <span style={{ color: '#909399' }}>{JSON.stringify(navigator.languages || [])}</span>
          </p>
          <p>
            <strong>所有偵測到的語言來源:</strong>
          </p>
          <ul style={{ listStyleType: 'none', paddingLeft: 10, marginTop: 5 }}>
            {browserLanguages.map((item, index) => (
              <li key={index} style={{ marginBottom: 5 }}>
                <span style={{ color: '#DCDFE6' }}>{item}</span>{' '}
                {mapToSupportedLanguage(item) ? (
                  <span style={{ color: '#4ecca3' }}>(映射: {mapToSupportedLanguage(item)})</span>
                ) : (
                  <span style={{ color: '#e74c3c' }}>(不支援)</span>
                )}
              </li>
            ))}
          </ul>
          <p>
            <strong>支援的語言列表:</strong> <span style={{ color: '#909399' }}>{JSON.stringify(supportedLngs)}</span>
          </p>
          <p>
            <strong>User Agent:</strong>{' '}
            <span style={{ color: '#DCDFE6', wordBreak: 'break-all', fontSize: 10 }}>{navigator.userAgent}</span>
          </p>
        </div>
      </div>
    )
  }

  if (variant === 'floating') {
    return (
      <>
        <div ref={containerRef} style={{ position: 'fixed', top: '50%', right: 0, transform: 'translateY(-50%)', zIndex: 9999 }}>
          {showTip && !open && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                right: 44,
                transform: 'translateY(-50%)',
                zIndex: 10000,
                background: 'rgba(0,0,0,0.8)',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 6,
                fontSize: 12,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              Language / 語言
            </div>
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            title="Language / 語言"
            aria-label="toggle-language"
            style={{
              width: 36,
              height: 36,
              border: 'none',
              borderRadius: '6px 0 0 6px',
              background: '#2f3640',
              color: '#fff',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              lineHeight: 1,
              padding: 0,
            }}
          >
            {currentShortLabel}
          </button>

          {open && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                right: 44,
                transform: 'translateY(-50%)',
                minWidth: 160,
                background: '#fff',
                border: '1px solid #ebeef5',
                borderRadius: 8,
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                padding: 6,
              }}
            >
              {languages.map((lng) => (
                <button
                  key={lng.code}
                  type="button"
                  onClick={() => handleLanguageChange(lng.code)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: isLanguageSelected(lng.code) ? '#409EFF' : '#303133',
                    fontWeight: isLanguageSelected(lng.code) ? 600 : 400,
                  }}
                >
                  {lng.label}
                </button>
              ))}
              {showDebug && (
                <button
                  type="button"
                  onClick={() => {
                    setShowDebugPanel(true)
                    setOpen(false)
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: '#606266',
                    fontWeight: 400,
                    borderTop: '1px solid #ebeef5',
                    marginTop: 6,
                    paddingTop: 10,
                  }}
                >
                  🔧 Debug Panel
                </button>
              )}
            </div>
          )}
        </div>
        {showDebugPanel && renderDebugPanel()}
      </>
    )
  }

  return (
    <>
      <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button type="button" onClick={() => setOpen((v) => !v)} title="Language / 語言" style={baseButtonStyle}>
          <span role="img" aria-label="language">
            🌐
          </span>
          <span>Language / 語言</span>
          <span style={{ fontSize: 10, color: '#909399' }}>{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              marginTop: 6,
              minWidth: 160,
              background: '#fff',
              border: '1px solid #ebeef5',
              borderRadius: 8,
              boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
              padding: 6,
              zIndex: 10,
            }}
          >
            {languages.map((lng) => (
              <button
                key={lng.code}
                type="button"
                onClick={() => handleLanguageChange(lng.code)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: isLanguageSelected(lng.code) ? '#409EFF' : '#303133',
                  fontWeight: isLanguageSelected(lng.code) ? 600 : 400,
                }}
              >
                {lng.label}
              </button>
            ))}
            {showDebug && (
              <button
                type="button"
                onClick={() => {
                  setShowDebugPanel(true)
                  setOpen(false)
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: '#606266',
                  fontWeight: 400,
                  borderTop: '1px solid #ebeef5',
                  marginTop: 6,
                  paddingTop: 10,
                }}
              >
                🔧 Debug Panel
              </button>
            )}
          </div>
        )}
      </div>
      {showDebugPanel && renderDebugPanel()}
    </>
  )
}

LanguageDropdown.propTypes = {
  variant: PropTypes.oneOf(['inline', 'floating']),
  showDebug: PropTypes.bool,
}

export default LanguageDropdown
